/**
 * Framework do tour guiado de onboarding.
 *
 * `TourProvider` fica montado uma vez, dentro do DashboardShell (ver
 * apps/web/src/components/layout/dashboard-shell.tsx), e cuida de:
 *  - decidir se um modulo tem tour pendente (para o indicador da sidebar);
 *  - iniciar um tour quando pedido (sidebar ou modal de boas-vindas),
 *    esperando a navegacao/DOM da rota alvo estabilizar;
 *  - renderizar o overlay com spotlight + popover do passo atual;
 *  - persistir a conclusao via PATCH /me/onboarding.
 *
 * Posicionamento do popover: card SEMPRE centralizado horizontalmente, em
 * uma de duas faixas fixas (topo ou base da viewport) — nao mais ancorado
 * ao lado do elemento em destaque. Decisao do dono em 2026-08-06 apos
 * feedback de producao: com o posicionamento ancorado, o card ficava
 * cortado/escondido em cantos da tela (ex: itens no fim da ConfigSidebar).
 * O publico do sistema tem dificuldade com interfaces — obvio e legivel
 * importa mais que elegante. A faixa (topo/base) e decidida uma unica vez
 * por passo, no momento em que o alvo resolve (ver `bandDecidedRef`), e nao
 * muda mais durante scroll/resize — evita o card "pular" de lugar. Sem depender
 * de medida do proprio card: cada faixa usa `top`/`bottom` fixos em vez de
 * calcular a partir da altura real, entao nunca corta (o CSS cuida disso
 * nativamente).
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { TourKey } from '@proadv/types';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/utils';
import { getTourDefinition, type TourStep } from './tour-registry';

interface ActiveTour {
  key: TourKey;
  stepIndex: number;
  /** Pathname no instante em que o tour foi montado — usado para detectar navegação real. */
  startPathname: string;
}

interface TourContextValue {
  /** Falso quando o tour ja foi concluido/pulado ou o usuario pediu "Não mostrar dicas". */
  isTourPending: (key: TourKey) => boolean;
  /** Pede para iniciar um tour. Se a rota alvo nao for a atual, espera a navegacao. */
  requestTour: (key: TourKey) => void;
  /**
   * Forca o inicio de um tour mesmo que ja esteja em `completed` — usado
   * pelo botao "Rever" em Configuracoes > Tour do sistema. Mesma mecanica
   * de `requestTour` (espera a navegacao ate a rota do modulo antes de
   * abrir o overlay); a unica diferenca e semantica, ja que `requestTour`
   * nunca checou `completed` pra comecar — quem faz essa checagem e o
   * chamador (ex: o indicador da sidebar so chama requestTour quando
   * isTourPending e verdadeiro). Concluir de novo e idempotente: como o
   * tour ja esta em `completed`, `finish()` nao dispara PATCH nenhum.
   */
  reviewTour: (key: TourKey) => void;
  activeKey: TourKey | null;
}

const TourContext = React.createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = React.useContext(TourContext);
  if (!ctx) {
    throw new Error('useTour precisa ser usado dentro de <TourProvider>');
  }
  return ctx;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const onboarding = useAuthStore((s) => s.onboarding);
  const setOnboarding = useAuthStore((s) => s.setOnboarding);

  const [active, setActive] = React.useState<ActiveTour | null>(null);
  /**
   * `fromPathname` = pathname no instante em que o tour foi pedido (sidebar,
   * modal de boas-vindas ou "Rever"). Usado por `tryStartPending` pra
   * distinguir "cheguei de verdade na rota do tour" de "ja estava, por
   * coincidencia, numa subrota cujo prefixo bate com a rota alvo sem ter
   * navegado pra la" — ver comentario dentro de `tryStartPending`.
   */
  const pendingRef = React.useRef<{ key: TourKey; fromPathname: string } | null>(null);
  const pendingRafIdsRef = React.useRef<number[]>([]);

  const patchMutation = useMutation({
    mutationFn: (patch: { completeTour: TourKey }) =>
      apiClient.meUpdateOnboarding(patch),
    onSuccess: (next) => setOnboarding(next),
  });

  const isTourPending = React.useCallback(
    (key: TourKey) =>
      !onboarding.dismissedAll && !onboarding.completed.includes(key),
    [onboarding],
  );

  const tryStartPending = React.useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    const { key, fromPathname } = pending;
    const def = getTourDefinition(key);
    const atTarget =
      location.pathname === def.route || location.pathname.startsWith(`${def.route}/`);
    if (!atTarget) return; // ainda navegando para a rota do tour

    // Guarda contra prefixo coincidente: algumas paginas de onde da pra
    // pedir um tour moram numa subrota que ja bate com o prefixo da rota
    // alvo antes de qualquer navegacao acontecer — ex: "Rever" de
    // Configuracoes e pedido em /escritorio/configuracoes/tour, que ja
    // "prefix-casa" com a rota do proprio tour de Configuracoes
    // (/escritorio/configuracoes). Se o pathname atual e EXATAMENTE o
    // mesmo de onde o pedido partiu, ainda nao houve navegacao de
    // verdade — espera o proximo disparo deste callback (o effect abaixo
    // reexecuta sempre que location.pathname muda de verdade).
    if (location.pathname !== def.route && location.pathname === fromPathname) {
      return;
    }

    // Cancela tentativas anteriores ainda pendentes: rotas com index-redirect
    // (ex: /escritorio/configuracoes -> .../grupos-de-acao) disparam este
    // callback mais de uma vez em sequencia rapida (pathname muda 2x antes
    // de assentar).
    for (const id of pendingRafIdsRef.current) cancelAnimationFrame(id);
    pendingRafIdsRef.current = [];
    // 2 frames pra dar tempo do DOM da rota nova (ou do modal fechando)
    // renderizar antes de procurar os seletores `data-tour`.
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => {
        if (pendingRef.current?.key !== key) return;
        // Le o pathname REAL na hora de ativar (window.location, nao o
        // `location.pathname` fechado no closure de quando os RAFs foram
        // agendados): rotas com index-redirect podem assentar ENTRE o
        // agendamento e a execucao dos 2 frames. Usar o valor antigo
        // gravava um `startPathname` que o efeito de cancelamento por
        // navegacao (abaixo) via divergir um instante depois — o tour
        // nascia e morria na mesma fracao de segundo (mesmo sintoma do
        // bug historico de Configuracoes, causa distinta e mais geral:
        // acontece em qualquer rota alvo que redireciona internamente).
        const currentPathname = window.location.pathname;
        const stillAtTarget =
          currentPathname === def.route || currentPathname.startsWith(`${def.route}/`);
        pendingRef.current = null;
        if (!stillAtTarget) return; // navegou pra outro lugar enquanto esperava
        setActive({ key, stepIndex: 0, startPathname: currentPathname });
      });
      pendingRafIdsRef.current.push(id2);
    });
    pendingRafIdsRef.current.push(id1);
  }, [location.pathname]);

  const requestTour = React.useCallback(
    (key: TourKey) => {
      pendingRef.current = { key, fromPathname: location.pathname };
      tryStartPending();
    },
    [location.pathname, tryStartPending],
  );

  React.useEffect(() => {
    tryStartPending();
  }, [tryStartPending]);

  // Navegacao real com tour ativo cancela o tour (sem marcar completed): o
  // indicador da sidebar continua pendente e o usuario pode refazer depois.
  // `active.startPathname` so e igual a `location.pathname` no instante em
  // que o tour acabou de montar (tryStartPending grava o pathname corrente
  // ali) — por isso esse effect nao dispara na propria navegacao que
  // iniciou o tour (ex: clique na sidebar), so em navegacoes DEPOIS disso.
  React.useEffect(() => {
    if (!active) return;
    if (location.pathname === active.startPathname) return;
    setActive(null);
  }, [location.pathname, active]);

  const finish = React.useCallback(
    (key: TourKey) => {
      setActive(null);
      if (!onboarding.completed.includes(key)) {
        patchMutation.mutate({ completeTour: key });
      }
    },
    [onboarding.completed, patchMutation],
  );

  const def = active ? getTourDefinition(active.key) : null;
  const step = def && active ? (def.steps[active.stepIndex] ?? null) : null;

  const goNext = React.useCallback(() => {
    if (!active || !def) return;
    if (active.stepIndex + 1 >= def.steps.length) {
      finish(active.key);
      return;
    }
    setActive({
      key: active.key,
      stepIndex: active.stepIndex + 1,
      startPathname: active.startPathname,
    });
  }, [active, def, finish]);

  const goPrev = React.useCallback(() => {
    if (!active) return;
    setActive({
      key: active.key,
      stepIndex: Math.max(0, active.stepIndex - 1),
      startPathname: active.startPathname,
    });
  }, [active]);

  const goSkip = React.useCallback(() => {
    if (!active) return;
    finish(active.key);
  }, [active, finish]);

  const value = React.useMemo<TourContextValue>(
    () => ({
      isTourPending,
      requestTour,
      // Mesma funcao: tryStartPending nunca checou `completed`, quem faz
      // esse gate e o chamador (sidebar). Alias com nome proprio so pra
      // deixar a intencao explicita no call site do botao "Rever".
      reviewTour: requestTour,
      activeKey: active?.key ?? null,
    }),
    [isTourPending, requestTour, active],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {step && active && def && (
        <TourOverlay
          step={step}
          stepIndex={active.stepIndex}
          totalSteps={def.steps.length}
          onNext={goNext}
          onPrev={goPrev}
          onSkip={goSkip}
        />
      )}
    </TourContext.Provider>
  );
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Faixa onde o card aparece: sempre o lado oposto ao do alvo, pra nao cobri-lo. */
type Band = 'top' | 'bottom';

// Margens fixas — o card usa `top`/`bottom` do CSS, nunca calcula a partir
// da propria altura, entao nao ha como "cortar": o navegador encolhe o
// espaco disponivel (max-height + overflow) em vez de estourar a viewport.
const SIDE_MARGIN = 16;
// Faixa do topo comeca abaixo da topbar fixa (h-16 = 64px) pra nao cobrir
// busca/notificacoes.
const TOP_BAND_OFFSET = 80;
const BOTTOM_BAND_OFFSET = 16;
const CARD_MAX_WIDTH = 460;

function TourOverlay({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
}: {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}) {
  const [rect, setRect] = React.useState<Rect | null>(null);
  const [band, setBand] = React.useState<Band | null>(null);
  // Trava a decisao de faixa em UMA por passo: setada na 1a medida bem
  // sucedida depois da troca de passo, ignorada nas remedidas seguintes
  // (scroll/resize) — sem isso o card ficaria pulando de faixa enquanto a
  // pagina rola.
  const bandDecidedRef = React.useRef(false);

  const updateRect = React.useCallback((): boolean => {
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) {
      setRect(null);
      return false;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    if (!bandDecidedRef.current) {
      bandDecidedRef.current = true;
      const targetMid = r.top + r.height / 2;
      // Alvo na metade superior da tela -> card na faixa de baixo, e
      // vice-versa. Decisao puramente geometrica, sem medir o card.
      setBand(targetMid < window.innerHeight / 2 ? 'bottom' : 'top');
    }
    return true;
  }, [step.target]);

  // Troca de passo: reseta a faixa decidida, scrolla ate o alvo (se
  // precisar) e mede depois da animacao de scroll assentar. Se o alvo nao
  // aparecer em nenhuma das 3 tentativas (60/280/700ms) — ex: item de
  // sidebar escondido por falta de modulo contratado, ou lista que ainda
  // esta carregando — pula o passo automaticamente em vez de travar o
  // tour. Checagem direta no DOM a cada tentativa (nao depende de mudanca
  // de estado do React, que nao dispara de novo quando o alvo segue
  // ausente entre uma tentativa e outra).
  React.useEffect(() => {
    let cancelled = false;
    bandDecidedRef.current = false;
    setBand(null);
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });

    function attempt(isLast: boolean) {
      if (cancelled) return;
      const found = updateRect();
      if (!found && isLast) {
        onNext();
      }
    }

    const t1 = window.setTimeout(() => attempt(false), 60);
    const t2 = window.setTimeout(() => attempt(false), 280);
    const t3 = window.setTimeout(() => attempt(true), 700);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [step.target, updateRect, onNext]);

  // Spotlight (anel + "buraco" no overlay) continua acompanhando o alvo
  // durante scroll/resize — so a faixa do card e que fica travada.
  React.useEffect(() => {
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [updateRect]);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onSkip();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSkip]);

  // So renderiza depois que alvo E faixa estao resolvidos — evita um
  // primeiro paint com o card na faixa errada (ou sem faixa nenhuma).
  if (!rect || !band) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const bandStyle: React.CSSProperties =
    band === 'top' ? { top: TOP_BAND_OFFSET } : { bottom: BOTTOM_BAND_OFFSET };

  return createPortal(
    <div className="fixed inset-0 z-[80]" aria-live="polite">
      {/* "Buraco" no overlay escuro: 4 retangulos ao redor do alvo, que
          tambem bloqueiam clique fora dele. O alvo em si fica livre. */}
      <div
        className="fixed bg-black/55"
        style={{ top: 0, left: 0, right: 0, height: Math.max(rect.top, 0) }}
      />
      <div
        className="fixed bg-black/55"
        style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }}
      />
      <div
        className="fixed bg-black/55"
        style={{ top: rect.top, left: 0, width: Math.max(rect.left, 0), height: rect.height }}
      />
      <div
        className="fixed bg-black/55"
        style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }}
      />

      {/* Anel de destaque em volta do alvo. */}
      <div
        className="pointer-events-none fixed rounded-md ring-2 ring-[var(--accent)]"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          boxShadow: '0 0 0 4px color-mix(in oklch, var(--accent) 30%, transparent)',
        }}
      />

      {/* Card sempre centralizado horizontalmente, numa faixa fixa (topo
          ou base da viewport) — nunca ancorado ao alvo. `top`/`bottom` sao
          valores fixos do CSS (nao calculados a partir da altura do card),
          entao o navegador nunca deixa o card estourar a viewport: quando
          o conteudo nao cabe, `max-height` + `overflow-y: auto` entram em
          jogo em vez de cortar/vazar por cima de outra faixa. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={step.title}
        className={cn(
          'popover-menu fixed left-1/2 z-[81] -translate-x-1/2 overflow-y-auto rounded-xl border p-6',
          'border-[var(--popover-border,var(--card-border))]',
        )}
        style={{
          ...bandStyle,
          width: `calc(100vw - ${SIDE_MARGIN * 2}px)`,
          maxWidth: CARD_MAX_WIDTH,
          maxHeight: `calc(100vh - ${TOP_BAND_OFFSET + BOTTOM_BAND_OFFSET}px)`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-[var(--fg)]">{step.title}</h3>
          <button
            type="button"
            onClick={onSkip}
            aria-label="Pular tour"
            className="shrink-0 rounded-md p-1.5 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-3 text-base leading-relaxed text-[var(--fg-muted)]">{step.body}</p>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-[var(--fg-muted)]">
            {stepIndex + 1} de {totalSteps}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSkip}
              className="rounded-md px-3 py-2.5 text-sm font-medium text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]"
            >
              Pular
            </button>
            {!isFirst && (
              <button
                type="button"
                onClick={onPrev}
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-4 py-2.5 text-sm font-medium text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]"
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </button>
            )}
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-4 py-2.5 text-sm font-medium text-[var(--accent-fg)] hover:opacity-90"
            >
              {isLast ? 'Concluir' : 'Próximo'}
              {!isLast && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
